import React from 'react'
import styles from './InvoiceDetails.module.scss'
import Layout from '../../components/core/Layout';
import { useParams } from 'react-router-dom';
import { getTaxRate, postWrappingFee, getSizeOptions, getDeliveryOptions, getWrappingFee } from '../../apis/invoice-api';
import { INVOICE_TAB_INDEX, InvStatus } from '../../libs/constants';
import { str2number } from '../../libs/helpers';
import { InvoiceMasterData } from "../../apis/models/InvoiceMasterRequest";
import { error, success } from '../../apis/reducers/snackbarsReducer';
import { useDispatch, useSelector } from 'react-redux';
import { getInvoiceMaster, putInvoiceMaster } from '../../apis/invmaster-api';
import { TaxResponseData } from '../../apis/models/TaxResponse';
import DateWithoutTime from '../../libs/dateWithoutTime';
import InvoiceHeader from '../../components/InvoiceDetailsPage/header/InvoiceHeader';
import { RootState } from '../../apis/store';
import { saveMasterInfo } from '../../apis/reducers/invoiceMasterReducer';
import WrappingFee, { WrappingFeeTabType } from '../../components/InvoiceDetailsPage/WrappingFee/WrappingFee';
import { DeliveryOptionResponseData, SizeOptionResponseData, WrappingFeeResponseData } from '../../apis/models/WrappingFeeResponse';
import { WrappingFeeRequestItem } from '../../apis/models/WrappingFeeRequest';

const wrappingFeeCompare = (a: WrappingFeeTabType, b: WrappingFeeTabType) => {
  return DateWithoutTime.sortAsc(a.dateAdded, b.dateAdded)
}

const InvoiceWrappingFeePage = () => {
  const invoiceMasterState = useSelector((state: RootState) => state.invoiceMaster);
  const dispatch = useDispatch();
  const { id } = useParams();
  const [invoiceMasterData, setInvoiceMasterData] = React.useState<InvoiceMasterData | null>(invoiceMasterState.masterData);
  const [taxItem, setTaxItem] = React.useState<TaxResponseData | null>(null);
  const [wrappingSizeOptions, setWrappingSizeOptions] = React.useState<SizeOptionResponseData[]>([]);
  const [wrappingDeliveryOptions, setWrappingDeliveryOptions] = React.useState<DeliveryOptionResponseData[]>([]);
  const [wrappingFees, setWrappingFees] = React.useState<WrappingFeeTabType[]>([]);
  const [notYetSaved, setNotYetSaved] = React.useState<boolean>(false);
  const [isChanged, setIsChanged] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (id) {
      loadAllData();
    }
  },[id])

  const hasError = React.useMemo(() => {
    return wrappingFees.some(fee => {
      if (Object.keys(fee.error).length !== 0) {
        return (
          fee.error.dateAdded
          || fee.error.total 
          || fee.error.compareSizeAndDelivery 
          || fee.error.differenceInSalesNumber 
          || fee.error.sizeErrors.length > 0
          || fee.error.deliveryErrors.length > 0
        )
      } else return false;
    })
  },[wrappingFees]);

  const loadAllData = () => {
    getInvoiceMasterData();
    getWrappingFeeData();
    setNotYetSaved(false);
  }

  const onSaveAndCommit = async () => {
    const isSuccess = await saveWrappingFee();
    if (isSuccess){
      onChangeInvoiceStatus(InvStatus.CONFIRMED)
    }
  }

  const onChangeInvoiceStatus = async (status: InvStatus) => {
    try {
      await putInvoiceMaster(id!, {status: status})
      dispatch(success("請求ステータスを変更しました"));
      loadAllData();
    }catch (err) {
      dispatch(error("請求ステータスの変更に失敗しました"));
    }
  }

  const getInvoiceMasterData = () => {
    getInvoiceMaster(id)
    .then(function (response) {
        setInvoiceMasterData(response.data.data);
        dispatch(saveMasterInfo({masterData: response.data.data}))
        getTaxRate(new Date(response.data.data.from).toString())
          .then(res => setTaxItem(res.data.data))
          .catch(err => console.log(err))
    })
    .catch(function (error) {
        console.log(error);
    })
  }

  const getWrappingFeeData = () => {
    if (!id) {
      return
    }
    const promises = [getSizeOptions(id), getDeliveryOptions(id), getWrappingFee(id)]
    Promise.all(promises)
      .then((values) => {
        setWrappingSizeOptions(values[0].data.data);
        setWrappingDeliveryOptions(values[1].data.data);
        setWrappingFees(convertResponseToTabType(values[2].data.data));
      })
      .catch((err) => {})
  }

  const convertResponseToTabType = (datas: WrappingFeeResponseData[]): WrappingFeeTabType[] => {
    const result: WrappingFeeTabType[] = [];
    datas.forEach((fee: WrappingFeeResponseData) => {
      const exists = result.find(r => r.dateAdded.equals(new DateWithoutTime(fee.dateAdded)))
      if (exists) {
        if (fee.wrappingSizeOptionId) {
          exists.wrappingSizeList.push({
            wrappingSizeOptionId: fee.wrappingSizeOptionId,
            wrappingSizeOption: fee.wrappingSizeOption!,
            quantity: fee.quantity + "",
          })
        } else if (fee.deliveryMethodOptionId) {
          exists.deliveryMethodList.push({
            deliveryMethodOptionId: fee.deliveryMethodOptionId,
            deliveryMethodOption: fee.deliveryMethodOption!,
            quantity: fee.quantity + "",
          })
        }
      } else {
        if (fee.wrappingSizeOptionId) {
          result.push({
            dateAdded: new DateWithoutTime(fee.dateAdded),
            memo: fee.memo,
            differenceInSalesNumber: fee.differenceInSalesNumber + "",
            wrappingSizeList: [{
              wrappingSizeOptionId: fee.wrappingSizeOptionId,
              wrappingSizeOption: fee.wrappingSizeOption!,
              quantity: fee.quantity + "",
            }],
            deliveryMethodList: [],
            error: {deliveryErrors: [], sizeErrors: []}
          })
        } else if (fee.deliveryMethodOptionId) {
          result.push({
            dateAdded: new DateWithoutTime(fee.dateAdded),
            memo: fee.memo,
            differenceInSalesNumber: fee.differenceInSalesNumber + "",
            wrappingSizeList: [],
            deliveryMethodList: [{
              deliveryMethodOptionId: fee.deliveryMethodOptionId,
              deliveryMethodOption: fee.deliveryMethodOption!,
              quantity: fee.quantity + "",
            }],
            error: {deliveryErrors: [], sizeErrors: []}
          })
        }
      }
    })
    result.sort(wrappingFeeCompare);
    return result;
  }

  const onChangeFees = (items: WrappingFeeTabType[]) => {
    setWrappingFees(items);
    setNotYetSaved(true);
  }

  const saveWrappingFee = async () => {
    if (!id) {
      return false;
    }
    const req: WrappingFeeRequestItem[] = [];
    wrappingFees.forEach(rowData => {
      rowData.wrappingSizeList.forEach(fee => {
        const reqItem: WrappingFeeRequestItem = {
          invoiceMasterId: id,
          dateAdded: rowData.dateAdded.getValue4Server(),
          differenceInSalesNumber: str2number(rowData.differenceInSalesNumber),
          memo: rowData.memo,
          quantity: str2number(fee.quantity),
          wrappingSizeOptionId: fee.wrappingSizeOptionId,
        }
        req.push(reqItem);
      })
      rowData.deliveryMethodList.forEach(fee => {
        const reqItem: WrappingFeeRequestItem = {
          invoiceMasterId: id,
          dateAdded: rowData.dateAdded.getValue4Server(),
          differenceInSalesNumber: str2number(rowData.differenceInSalesNumber),
          memo: rowData.memo,
          quantity: str2number(fee.quantity),
          deliveryMethodOptionId: fee.deliveryMethodOptionId,
        }
        req.push(reqItem);
      })
    });

    try {
      await postWrappingFee(id, req);
      dispatch(success("保存しました"));
      loadAllData();

      return true;
    } catch (e) {
      dispatch(error("保存に失敗しました"))
    }
    return false;
  }

  return (
    <Layout withBreadcrumbs={true} bgColor="#FAFAFA">
      <React.Fragment>
        <div className={styles.Gray}>
          <div>
            <InvoiceHeader
              invoiceMaster={invoiceMasterData ?? undefined}
              tax={taxItem ?? undefined}
              selectedTab={INVOICE_TAB_INDEX.WRAPPING}
              needSave={notYetSaved}
              error={hasError}
              onSaveAndCommit={() => onSaveAndCommit()}
              onChangeStatus={(status) => onChangeInvoiceStatus(status)}
              onMasterChange={() => loadAllData()}
            />
          </div>
          <div  className={styles.tabs}>
            <div className={styles.tabs__body}>
              {invoiceMasterData &&
                <WrappingFee
                  targetInvoice={invoiceMasterData}
                  tabValues={wrappingFees}
                  wrappingSizeOptions={wrappingSizeOptions}
                  wrappingDeliveryOptions={wrappingDeliveryOptions}
                  isChanged={isChanged}
                  onNotYetConfirmed={() => onChangeInvoiceStatus(InvStatus.NOT_CONFIRMED)}
                  onReset={() => loadAllData()}
                  onSave={() => saveWrappingFee()}
                  onChange={(items) => {onChangeFees(items); setIsChanged(true)}}
                />
              }
            </div>
          </div>
        </div>
      </React.Fragment>
    </Layout>
  )
}

export default InvoiceWrappingFeePage